import {
  InfoOutlined,
  SettingsBackupRestoreOutlined,
} from '@mui/icons-material';
import { Box, Button, IconButton, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';
import CircularLoader from '../../../atoms/CircularLoader/circular-loader';
import ListView from '../../../atoms/DataList/ListView';
import {
  DebounceTimer,
  LoadingStatus,
  ToastThemes,
} from '../../../constants/common';
import { DateRangePro } from '../../molecules/DateRange/DateRangePro';
import {
  firstDateOfCurrentMonth,
  getFormattedValues,
  getMonthRange,
  getMonthRangeMoment,
  lastDateOfCurrentMonth,
} from '../../../utils/formatter';
import { showToast } from '../../../utils/common.util';
import { GeoTagging } from '../../../services/geoTagging';
import { permissionsObj } from '../../OnBoarding/Organization/RolesAndPermissions/constants/roles-permissions.constants';
import { EmptyDashboardMsg } from '../Constant/Constant';
import ModalMapForAttendance from './DasboardModalMap/ModalMapForAttendance';
import moment from 'moment';

const AllAttendanceRegister = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [checkedItems, setCheckedItems] = useState([]);
  const [selectedDate, setSelectedDate] = useState([
    firstDateOfCurrentMonth,
    lastDateOfCurrentMonth,
  ]);
  const [loading, setLoading] = useState(-1);
  const [data, setData] = useState([]);
  const [attendanceIdPropertiesMap, setAttendanceIdPropertiesMap] = useState(
    {}
  );

  const { currentUserRoleId } = useSelector((state) => state.main);
  const { timeManagementPermission } = useSelector(
    (state) => state.modulePermissions
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const searchInput = searchParams.get('q');
  const debouncedVal = useDebounce(searchInput, DebounceTimer);
  const [filteredList, setFilteredList] = useState(data);
  const eId = searchParams.get('eId');
  const date = searchParams.get('date');
  const range = getMonthRange(date);
  const rangeMoment = getMonthRangeMoment(date);

  useEffect(async () => {
    if (currentUserRoleId) {
      setLoading(LoadingStatus.Loading);
      await new GeoTagging()
        .listAllRegister(
          currentUserRoleId,
          searchInput,
          date
            ? range.startDate
            : getFormattedValues('YYYY-MM-DD', selectedDate[0]),
          date
            ? range.endDate
            : getFormattedValues('YYYY-MM-DD', selectedDate[1])
        )
        .then((res) => {
          setLoading(LoadingStatus.Success);
          setData(res);
          const newMap = {};
          res?.forEach((item) => {
            newMap[item.pkId] = {
              empStatusCode: item.fkEmployeeStatusCode?.name,
              mgrStatusCode: item.fkManagerStatusCode?.name,
              hrStatusCode: item.fkFinalStatusCode?.name,
            };
          });
          setAttendanceIdPropertiesMap(newMap);
        })
        .catch((err) => {
          console.log(err);
          showToast(
            err?.response?.data?.message || 'Something Went Wrong! Try Again',
            ToastThemes.error
          );
          setLoading(LoadingStatus.Failure);
        });
    }
  }, [currentUserRoleId, debouncedVal, selectedDate, date]);

  useEffect(() => {
    setCheckedItems([]);
  }, [filteredList]);

  useEffect(() => {
    if (date) {
      setSelectedDate([rangeMoment.startDate, rangeMoment.endDate]);
    }
  }, [date]);

  useEffect(() => {
    if (eId) {
      const newList = data.filter((item) => {
        const { fkUserId } = item;
        return Number(eId) === fkUserId?.id;
      });
      setFilteredList(newList);
    } else {
      setFilteredList(data);
    }
  }, [eId, data]);

  const rowData = filteredList?.map((item) => {
    const {
      pkId,
      fkUserId,
      schedule,
      calendarDate,
      attendanceRegisterStatus,
      fkEmployeeStatusCode,
      fkFinalStatusCode,
      fkManagerStatusCode,
      attendenceRelatedInformation,
    } = item;
    return {
      attat: calendarDate ? calendarDate : '',
      empId:
        timeManagementPermission?.['attendance']?.viewAccess === 1
          ? { hide: true }
          : fkUserId.employeeId || '',
      empName:
        timeManagementPermission?.['attendance']?.viewAccess === 1
          ? { hide: true }
          : fkUserId.displayName || '',
      work:
        timeManagementPermission?.['attendance']?.viewAccess === 1
          ? { hide: true }
          : schedule
          ? `${schedule.name} (${schedule.code})`
          : '',
      ot:
        timeManagementPermission?.['attendance']?.viewAccess === 1 &&
        !attendenceRelatedInformation?.otApplicable
          ? { hide: true }
          : attendenceRelatedInformation?.overtime || 0,
      source: attendenceRelatedInformation?.attendanceType || '',
      overallStatus: attendenceRelatedInformation?.registerStatus || '',
      empstatus: fkEmployeeStatusCode?.name || 'Pending',
      mgrstatus: fkManagerStatusCode?.name || 'Pending',
      status: fkFinalStatusCode?.name || 'Pending',
      action: (
        <IconButton onClick={() => navigate({ pathname: `register/${pkId}` })}>
          <InfoOutlined sx={{ color: 'primary.main80' }} />
        </IconButton>
      ),
      id: {
        hide: true,
        value: pkId,
      },
    };
  });

  const isDisabledReset = () => {
    return (
      moment(selectedDate[0]).isSame(firstDateOfCurrentMonth) &&
      moment(selectedDate[1]).isSame(lastDateOfCurrentMonth)
    );
  };

  const resetFilters = () => {
    searchParams.delete('date');
    setSearchParams(searchParams);
    setSelectedDate([firstDateOfCurrentMonth, lastDateOfCurrentMonth]);
  };

  const handleClear = (event) => {
    event.stopPropagation();
    searchParams.delete('eId');
    searchParams.delete('date');
    setSearchParams(searchParams);
    setSelectedDate([firstDateOfCurrentMonth, lastDateOfCurrentMonth]);
  };

  return (
    <>
      <CircularLoader show={loading === LoadingStatus.Loading} />
      <Box sx={{ backgroundColor: 'white' }}>
        <Box display="flex" justifyContent="flex-end" gap={2} mb={1} p={2}>
          <DateRangePro
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
          <Button
            startIcon={<SettingsBackupRestoreOutlined />}
            sx={{ textTransform: 'capitalize', mt: -1.5 }}
            disabled={isDisabledReset()}
            onClick={resetFilters}
          >
            Clear
          </Button>
          <ModalMapForAttendance
            checkedItems={checkedItems}
            searchInput={searchInput}
            currentUserRoleId={currentUserRoleId}
            selectedDate={selectedDate}
            setData={setData}
            attendanceIdPropertiesMap={attendanceIdPropertiesMap}
            setAttendanceIdPropertiesMap={setAttendanceIdPropertiesMap}
          />
        </Box>
        <ListView
          headCells={[
            {
              id: 'attat',
              label: 'Attendance Date',
              dataType: 'date',
              dateFormat: 'YYYY-MM-DD',
            },
            {
              id: 'empId',
              label: 'Employee Id',
              hide: timeManagementPermission?.['attendance']?.viewAccess === 1,
              showFilterIcon: eId ? true : false,
              clear: (event) => handleClear(event),
            },
            {
              id: 'empName',
              label: 'Employee Name',
              hide: timeManagementPermission?.['attendance']?.viewAccess === 1,
            },
            {
              id: 'work',
              label: 'Work Shift',
              hide: timeManagementPermission?.['attendance']?.viewAccess === 1,
            },
            {
              id: 'ot',
              label: 'Overtime Hours',
              hide:
                timeManagementPermission?.['attendance']?.viewAccess === 1 &&
                !data?.[0]?.attendenceRelatedInformation?.otApplicable,
            },
            {
              id: 'source',
              label: 'Source',
            },
            {
              id: 'overallStatus',
              label: 'Overall Status',
            },
            {
              id: 'empstatus',
              label: 'Employee Status',
            },
            {
              id: 'mgrstatus',
              label: 'Manager Status',
            },
            {
              id: 'status',
              label: 'Approval Status',
            },
          ]}
          rowsData={rowData}
          showSerialNo={true}
          showCheckbox={true}
          setCheckedItems={setCheckedItems}
          checkedItems={checkedItems}
          allowSelectAll={true}
          showAddIcon={
            timeManagementPermission?.['attendance']?.[
              permissionsObj.allowAdd
            ] || false
          }
          onAddClick={() => navigate('m')}
          emptyDashboardMsg={EmptyDashboardMsg}
        />
      </Box>
    </>
  );
};

export default AllAttendanceRegister;
